import { http } from '@/api/client/httpClient';
import type { PagedResult } from '@/api/types/abp';
import type {
  ApiSyncResultDto,
  DeleteStatementInput,
  MatchRequestResultDto,
  MatchTransactionsInput,
  MatchedTransactionRow,
  ProviderStatementSummary,
  ProviderStatementTransactionsInput,
  ProviderTransactionDto,
  StatementCommentDto,
  StatementViewModel,
  StatementsInput,
  UnmatchTransactionsInput,
} from '@/api/types/providerStatements';

// ABP conventional routing for the per-provider *StatementAppService classes. The
// route base is the kebab of the concrete class name minus "AppService"
// (CmlChfStatementAppService -> cml-chf-statement, EcommPayUsdStatementAppService ->
// ecomm-pay-usd-statement). One factory instance per provider, keyed from the registry.
//
// Verb mapping (verified from the interface signatures):
//   Get*(scalar/none/complex)      -> GET  (prefix stripped, rest kebabed; DTO -> query)
//   Match/Unmatch(dto)             -> POST (DTO as JSON body)
//   DeleteStatement(dto)           -> DELETE with the DTO as the request body
//   UploadExcel(byte[], comment)   -> POST (bytes serialized as base64 JSON)
//   GetTemplate/GetExtract()       -> GET returning byte[] (base64 JSON -> Blob here)
//   ProcessApiStatements()         -> POST (EcommPay only)

// Repeated-param array serialization is irrelevant here (no List<T> query params);
// the inputs are flat objects bound to query/body fields directly.

function base64ToBlob(base64: string, contentType: string): Blob {
  const binary = atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: contentType });
}

async function fileToBase64(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

const XLSX_CONTENT_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

function triggerDownload(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export interface ProviderStatementApi<TStatement extends StatementViewModel = StatementViewModel> {
  base: string;
  providerExists: () => Promise<boolean>;
  getSummaryData: () => Promise<ProviderStatementSummary>;
  getUnmatchedStatements: (input: StatementsInput) => Promise<PagedResult<TStatement>>;
  getUnmatchedTransactions: (
    input: ProviderStatementTransactionsInput,
  ) => Promise<PagedResult<ProviderTransactionDto>>;
  getMatchedTransactions: () => Promise<MatchedTransactionRow<TStatement>[]>;
  matchTransactions: (input: MatchTransactionsInput) => Promise<MatchRequestResultDto>;
  unmatchTransactions: (input: UnmatchTransactionsInput) => Promise<MatchRequestResultDto>;
  deleteStatement: (input: DeleteStatementInput) => Promise<void>;
  getUnmatchedTransactionsComments: (ticket: number) => Promise<StatementCommentDto[]>;
  getUnmatchedStatementsComments: (statementId: string) => Promise<StatementCommentDto[]>;
  getMatchedComments: (ticket: number, statementId: string) => Promise<StatementCommentDto[]>;
  uploadExcel: (file: File, comment: string) => Promise<void>;
  downloadTemplate: (fileName: string) => Promise<void>;
  downloadExtract: (fileName: string) => Promise<void>;
  // EcommPay only; resolved against IApiSyncableStatementAppService.
  processApiStatements?: () => Promise<ApiSyncResultDto>;
}

export function makeProviderStatementApi<TStatement extends StatementViewModel = StatementViewModel>(
  routeBase: string,
  supportsApiSync: boolean,
): ProviderStatementApi<TStatement> {
  const base = `/api/app/${routeBase}`;

  const api: ProviderStatementApi<TStatement> = {
    base,

    providerExists: () => http.get<boolean>(`${base}/provider-exists`).then((r) => r.data),

    getSummaryData: () =>
      http.get<ProviderStatementSummary>(`${base}/summary-data`).then((r) => r.data),

    getUnmatchedStatements: (input) =>
      http
        .get<PagedResult<TStatement>>(`${base}/unmatched-statements`, { params: input })
        .then((r) => r.data),

    getUnmatchedTransactions: (input) =>
      http
        .get<PagedResult<ProviderTransactionDto>>(`${base}/unmatched-transactions`, { params: input })
        .then((r) => r.data),

    getMatchedTransactions: () =>
      http.get<MatchedTransactionRow<TStatement>[]>(`${base}/matched-transactions`).then((r) => r.data),

    matchTransactions: (input) =>
      http.post<MatchRequestResultDto>(`${base}/match-transactions`, input).then((r) => r.data),

    unmatchTransactions: (input) =>
      http.post<MatchRequestResultDto>(`${base}/unmatch-transactions`, input).then((r) => r.data),

    deleteStatement: (input) =>
      http.delete<void>(`${base}/statement`, { data: input }).then((r) => r.data),

    getUnmatchedTransactionsComments: (ticket) =>
      http
        .get<StatementCommentDto[]>(`${base}/unmatched-transactions-comments`, { params: { ticket } })
        .then((r) => r.data),

    getUnmatchedStatementsComments: (statementId) =>
      http
        .get<StatementCommentDto[]>(`${base}/unmatched-statements-comments`, { params: { statementId } })
        .then((r) => r.data),

    getMatchedComments: (ticket, statementId) =>
      http
        .get<StatementCommentDto[]>(`${base}/matched-comments`, { params: { ticket, statementId } })
        .then((r) => r.data),

    // AppService UploadExcelAsync(byte[] fileBytes, string comment) — ABP binds a
    // byte[] param from a base64 JSON field. NOTE: this path skips the Razor handler's
    // excel/csv validation + UploadStore retention (backend gap — see report).
    uploadExcel: async (file, comment) => {
      const fileBytes = await fileToBase64(file);
      await http.post<void>(`${base}/upload-excel`, { fileBytes, comment });
    },

    // GetTemplate/GetExtract return byte[] (base64 JSON over the proxy, not a
    // FileResult) — decode to a Blob and download with a fixed filename.
    downloadTemplate: async (fileName) => {
      const res = await http.get<string>(`${base}/template`);
      triggerDownload(base64ToBlob(res.data, XLSX_CONTENT_TYPE), fileName);
    },

    downloadExtract: async (fileName) => {
      const res = await http.get<string>(`${base}/extract`);
      triggerDownload(base64ToBlob(res.data, XLSX_CONTENT_TYPE), fileName);
    },
  };

  if (supportsApiSync) {
    api.processApiStatements = () =>
      http.post<ApiSyncResultDto>(`${base}/process-api-statements`).then((r) => r.data);
  }

  return api;
}
